// External libraries
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AiOutlineClose } from "react-icons/ai"
import { useRecoilState, useRecoilValue } from "recoil";

// Components
import { Body, BodyColor, BodyType, Button, ButtonTheme, ButtonVariant, Heading, TypographyColor, TypographyType } from "../../ui-elements"
import { POICategoryListItem } from "./POICategoryListItem";
import { cifTypeState, geoState, POICategories, selPOICategories } from "../../states";

interface POICategoryListHomeProps {
    isOpen: boolean;
    handleListOpen: (keyName: string, tagName?: string) => void;
    catCheckBoxRef: React.RefObject<HTMLDivElement>;
    buttonRef?: React.RefObject<HTMLDivElement>;
    isListOpen: { categories: boolean, brands: boolean };
    catchment?: string | null;
    isAggLayerOff?: boolean;
}

export const POICategoryListHome = ({ isOpen, handleListOpen, catCheckBoxRef, buttonRef, isListOpen, catchment, isAggLayerOff }: POICategoryListHomeProps) => {
    const localButtonRef = useRef<HTMLDivElement | null>(null);
    const listContainerRef = useRef<HTMLDivElement>(null);
    const categoryRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const poiCategories = useRecoilValue(POICategories);
    const [selCB, setSelCB] = useState<number | null>(null);
    const [selCategoryList, setSelCategoryList] = useRecoilState(selPOICategories);
    const [selTempCats, setSelTempCats] = useState(selCategoryList);
    const selectedGeo = useRecoilValue(geoState);
    const cifType = useRecoilValue(cifTypeState);

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setSelTempCats(checked ?
            poiCategories.reduce((acc: { category: number[]; subcategory: number[]; }, category) => {
                acc.category.push(category.key);

                if (category.sub_categories && category.sub_categories.length > 0) {
                    acc.subcategory.push(...category.sub_categories.map(sub => sub.key));
                }

                return acc;
            }, { category: [], subcategory: [] }) :
            { category: [], subcategory: [] });
    }

    const handleCategoryChange = (categoryKey: number) => {
        setSelCB(selCB === categoryKey ? null : categoryKey);
    }

    const handleCancelFilter = () => {
        setSelTempCats(selCategoryList);
        handleListOpen('categories');
    }

    const handleApplyFilter = () => {
        setSelCategoryList(selTempCats);
        localStorage.setItem('selCategoryList', JSON.stringify(selTempCats));
        handleListOpen('categories');
    }

    const updateTempCatList = (listName: string, list: number[]) => {
        setSelTempCats(prev => ({ ...prev, [listName]: list }));
    }

    useEffect(() => {
        if (selCategoryList) {
            setSelTempCats(selCategoryList);
        }
    }, [selCategoryList]);

    useEffect(() => {
        if (!isOpen) {
            setSelTempCats(selCategoryList);
            setSelCB(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (selCB !== null && listContainerRef.current) {
            const el = categoryRefs.current.get(selCB);
            if (el) {
                const container = listContainerRef.current;
                container.scrollTo({ top: el.offsetTop - 60, behavior: 'smooth' });
            }
        }
    }, [selCB]);

    const getPortalStyle = (): React.CSSProperties => {
        if (!localButtonRef.current) return {};
        const rect = localButtonRef.current.getBoundingClientRect();
        return {
            left: rect.right + 20,
            top: rect.top - 205,
        };
    };

    return (
        <div className={`m-0 p-0 position-relative col-12 ${isAggLayerOff ? 'disabled-heading' : ''}`}>
            {/* categories button */}
            <div
                ref={(el) => {
                    localButtonRef.current = el;
                    if (buttonRef) (buttonRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                }}
                className="d-flex justify-content-between align-items-center cursor-pointer padding-top-bottom-1 poi-category"
                onClick={(event) => handleListOpen('categories', (event.target as HTMLElement).tagName)}
            >
                <div className="d-flex justify-content-start align-items-center gap-1 col-11">
                    {/* {(!!selectedGeo.sub_district || catchment) && <span className="presence-indicator-hover-card category-indicator"></span>} */}
                    <Body
                        type={BodyType.p3}
                        color={BodyColor.secondary}
                        classname=""
                    >
                        Category Filter
                    </Body>
                </div>
                <span className="material-symbols-outlined fs-18 fw-bold icon-gray-0 p-0">keyboard_arrow_right</span>
            </div>
            {/* categories popup via portal — only render if this instance's button is visible */}
            {isListOpen.categories && localButtonRef.current?.offsetParent !== null &&
                createPortal(
                    <div ref={catCheckBoxRef} className="poi-category-list-home d-flex flex-column" style={getPortalStyle()}>
                        <div className="d-flex justify-content-between align-items-center border-bottom padding-top-bottom-4 padding-left-right-5">
                            <Body
                                type={BodyType.p3}
                                color={BodyColor.secondary}
                                classname="p-0"
                            >
                                Select Categories
                            </Body>
                            <AiOutlineClose className="fs-12 text-black cursor-pointer" onClick={handleCancelFilter} />
                        </div>
                        <div ref={listContainerRef} className="padding-top-bottom-2 no-scrollbar poi-cats-list-home">
                            <div className="d-flex justify-content-start align-items-center cursor-pointer padding-left-right-5 padding-top-bottom-3 poi-category">
                                <div className="width-10 d-flex justify-content-start align-items-center">
                                    <input
                                        className="p-0"
                                        type="checkbox"
                                        checked={poiCategories.length === selTempCats.category.length}
                                        onChange={(event) => handleSelectAll(event)}
                                    />
                                </div>
                                <Heading
                                    title='Select All'
                                    colour={TypographyColor.secondary}
                                    type={TypographyType.h6}
                                    classname='m-0 p-0 text-start width-90 d-flex justify-content-between align-items-center'
                                />
                            </div>
                            {poiCategories.map((category) => (
                                <div
                                    key={category.key}
                                    ref={(el) => { if (el) categoryRefs.current.set(category.key, el); else categoryRefs.current.delete(category.key); }}
                                    className={`cursor-pointer padding-left-right-5 padding-top-bottom-2 poi-category ${selCB === category.key ? 'poi-category-selected' : ''}`}
                                // onClick={(event) => handleCategoryChange(event, category.key)}
                                >
                                    <POICategoryListItem
                                        type='category'
                                        category={category}
                                        selCB={selCB}
                                        selTempCats={selTempCats}
                                        updateTempCatList={updateTempCatList}
                                        handleCategoryChange={handleCategoryChange}
                                    />

                                    {selCB === category.key && category.sub_categories?.map(sub_category => (
                                        <div key={sub_category.key} className="padding-left-4">
                                            <POICategoryListItem
                                                type='subcategory'
                                                category={sub_category}
                                                selCB={selCB}
                                                selTempCats={selTempCats}
                                                updateTempCatList={updateTempCatList}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                        <div className="d-flex justify-content-around align-items-center padding-top-bottom-3 border-top">
                            <Button
                                theme={ButtonTheme.primary}
                                variant={ButtonVariant.transparent}
                                type='button'
                                classname='padding-left-right-3'
                                onClick={handleCancelFilter}
                            >
                                Cancel
                            </Button>
                            <Button
                                theme={ButtonTheme.primary}
                                variant={ButtonVariant.bordered}
                                type='button'
                                classname='padding-left-right-5'
                                onClick={handleApplyFilter}
                                disabled={selTempCats.category.length === 0 && selTempCats.subcategory.length === 0}
                            >
                                Apply
                            </Button>
                        </div>
                    </div>,
                    document.body
                )
            }
        </div>
    )
}